 
import { createSlice } from '@reduxjs/toolkit';
import {
  ClinicalTrialInventoryState,
  ClinicalTrialInventoryFetchAction,
  ClinicalTrialInventoryFetchSuccessAction,
  ClinicalTrialInventoryItemDeleteAction,
  ClinicalTrialInventoryItemCreateAction,
  ClinicalTrialInventoryItemUpdateAction,
  ClinicalTrialInventoryFetchImgSpecSuccessAction,
} from './clinicalTrialInventory.types';
import { createSelector, createSliceHook, makeActionCreator } from '../helpers';

const initialState: ClinicalTrialInventoryState = {
  scoringForms: [],
  functions: [],
  instruments: [],
  measurements: [],
  readers: [],
  reportTemplates: [],
  tags: [],
  dictionaries: [],
  loading: false,
  error: undefined,
};

const { actReject, act, actReq, actResolve } =
  makeActionCreator<ClinicalTrialInventoryState>();

export const clinicalTrialInventorySlice = createSlice({
  name: 'clinicalTrialInventory',
  initialState,
  reducers: {
    fetchClinicalTrialInventory:
      actReq<ClinicalTrialInventoryFetchAction['payload']>(),
    fetchClinicalTrialUtilsInventory:
      actReq<ClinicalTrialInventoryFetchAction['payload']>(),
    fetchClinicalTrialInventorySuccess: act<
      ClinicalTrialInventoryFetchSuccessAction['payload']
    >(
      ({
        functions,
        reportTemplates,
        scoringForms,
        tags,
        instruments,
        measurements,
        readers,
      }) => ({
        functions,
        reportTemplates,
        instruments,
        measurements,
        readers,
        scoringForms,
        tags,
        loading: false,
      })
    ),
    fetchClinicalTrialInventoryFailure: actReject(),

    fetchImagingSpecialistInstruments:
      actReq<ClinicalTrialInventoryFetchAction['payload']>(),

    fetchImagingSpecialistInstrumentsSuccess: act<
      ClinicalTrialInventoryFetchImgSpecSuccessAction['payload']
    >(({ instruments }) => ({
      instruments,
      loading: false,
    })),
    fetchImagingSpecialistInstrumentsFailure: actReject(),

    deleteClinicalTrialInventoryItem:
      actReq<ClinicalTrialInventoryItemDeleteAction['payload']>(),
    deleteClinicalTrialInventoryItemSuccess: actResolve(),
    deleteClinicalTrialInventoryItemFailure: actReject(),
    createClinicalTrialInventoryItem:
      actReq<ClinicalTrialInventoryItemCreateAction['payload']>(),
    createClinicalTrialInventoryItemSuccess: actResolve(),
    createClinicalTrialInventoryItemFailure: actReject(),
    updateClinicalTrialInventoryItem:
      actReq<ClinicalTrialInventoryItemUpdateAction['payload']>(),
    updateClinicalTrialInventoryItemSuccess: actResolve(),
    updateClinicalTrialInventoryItemFailure: actReject(),
    reset: () => {
      return initialState;
    },
  },
  selectors: {
    selectClinicalTrialInventory: state => state,
  },
});

export const {
  fetchClinicalTrialInventory,
  fetchClinicalTrialUtilsInventory,
  fetchClinicalTrialInventorySuccess,
  fetchClinicalTrialInventoryFailure,
  deleteClinicalTrialInventoryItem,
  deleteClinicalTrialInventoryItemSuccess,
  deleteClinicalTrialInventoryItemFailure,
  createClinicalTrialInventoryItem,
  createClinicalTrialInventoryItemSuccess,
  createClinicalTrialInventoryItemFailure,
  updateClinicalTrialInventoryItem,
  updateClinicalTrialInventoryItemSuccess,
  updateClinicalTrialInventoryItemFailure,
} = clinicalTrialInventorySlice.actions;

export const actions = {
  ...clinicalTrialInventorySlice.actions,
};

export const useClinicalTrialInventorySlice = createSliceHook(
  clinicalTrialInventorySlice.name,
  actions,
  initialState
);
export const selectClinicalTrialInventoryState = createSelector(
  clinicalTrialInventorySlice.name
);
